import { Package, TramFront, Truck } from 'lucide-react';
import type { LogisticsIncoterm, VehicleType } from '~/graphql/graphql';
import {
  incoTermToDeliveryMethod,
  vehicleTypeToTransportMode,
} from '~/hooks/useTransportState';
import { calcItemLoads, getVehiclePayload } from '~/utils/order';
import type { CalcItemLoadsInput } from '~/utils/order';
import { useTranslation } from 'react-i18next';

interface TransportationSectionProps {
  items: CalcItemLoadsInput[];
  incoTerm: LogisticsIncoterm;
  vehicle?: { id: string; name: string; vehicleType: VehicleType } | null;
  tarp?: boolean;
}

export function TransportationSection({
  items,
  incoTerm,
  vehicle,
  tarp,
}: Readonly<TransportationSectionProps>) {
  const { t } = useTranslation(['cart', 'settings']);

  const vehicleId = vehicle?.id ?? '';

  const loads = items.reduce((acc, item) => {
    const vehiclePayload = getVehiclePayload(
      item.platformProductsLengths.platformProductVehicleCapacities,
      vehicleId
    );
    return acc + calcItemLoads(item, vehiclePayload);
  }, 0);

  const mode = vehicle
    ? vehicleTypeToTransportMode(vehicle.vehicleType)
    : 'truck';

  const count = Math.ceil(loads);
  const lastVehiclePct = loads > 0 ? Math.round((loads % 1 || 1) * 100) : 0;
  const modeConfig = {
    truck: {
      icon: Truck,
      unitLabel: t('cart.trucks_lowercase'),
      lastLabel: t('cart.last_truck'),
    },
    railcar: {
      icon: TramFront,
      unitLabel: t('cart.railcars_lowercase'),
      lastLabel: t('cart.last_railcar'),
    },
    container: {
      icon: Package,
      unitLabel: t('cart.containers_lowercase'),
      lastLabel: t('cart.last_container'),
    },
  } as const;
  const {
    icon: VehicleIcon,
    unitLabel,
    lastLabel: lastVehicleLabel,
  } = modeConfig[mode];
  // Only EXW ("pickup") gets its own label; every other incoterm (in practice
  // just DAP for domestic orders — this section is hidden for international
  // orders) displays as "delivered".
  const deliveryMethodLabel =
    incoTermToDeliveryMethod(incoTerm) === 'pickup'
      ? t('cart.delivery_method_pickup')
      : t('cart.delivery_method_delivered');

  return (
    <div
      data-testid="transportation-estimate"
      className="bg-neut-bg-alt rounded-lg border border-neut-200 p-6 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-4">
        <VehicleIcon className="w-5 h-5 text-success-500" aria-hidden="true" />
        <h2 className="text-neut-prim font-semibold">
          {t('cart.transportation_estimate')}
        </h2>
      </div>

      <div className="mb-6">
        <div className="bg-status-info-50 rounded-lg p-4 inline-flex gap-6 items-start">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <VehicleIcon className="w-4 h-4 text-status-info-600" />
              <span
                data-testid="transportation-estimate-vehicle"
                className="text-xs text-neut-600 font-normal"
              >
                {vehicle?.name ?? '—'}
              </span>
            </div>
            <div className="flex items-baseline gap-1 mb-1">
              <span
                data-testid="transportation-estimate-count"
                className="text-2xl font-semibold text-status-info-800"
              >
                {count}
              </span>
              <span className="text-xs font-normal text-status-info-600">
                {unitLabel}
              </span>
            </div>
            <p
              data-testid="transportation-estimate-loads"
              className="text-xs text-neut-500"
            >
              {loads.toFixed(2)} {t('cart.loads')}
            </p>
            {loads > 0 && (
              <p
                data-testid="transportation-estimate-last-vehicle"
                className="text-xs text-status-info-600 mt-1"
              >
                {lastVehicleLabel}: {lastVehiclePct}% {t('cart.full')}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-neut-200">
        <p className="text-sm text-neut-600 mb-3 font-medium">
          {t('cart.shipping_cost_breakdown')}
        </p>
        <div className="space-y-2 bg-neut-100 rounded-lg p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-neut-600">{t('cart.delivery_method')}</span>
            <span
              data-testid="transportation-estimate-delivery-method"
              className="font-medium text-success-600"
            >
              {deliveryMethodLabel}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-neut-600">{t('cart.transport_name')}</span>
            <span
              data-testid="transportation-estimate-transport-name"
              className="text-neut-prim font-medium"
            >
              {vehicle?.name ?? '—'}
            </span>
          </div>
          {tarp != null && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-neut-600">{t('cart.tarped')}</span>
              <span
                data-testid="transportation-estimate-tarped"
                className="font-medium text-success-600"
              >
                {tarp ? t('cart.yes') : t('cart.no')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
